import type { ComponentProps } from 'react'

import { Component } from 'react'
import { Checkbox, CheckboxGroup } from 'react-checkbox-group'
import { Radio, RadioGroup } from 'react-radio-group'

import { Input } from './Input'
import { StyledToggleComponent } from './InputToggle'
import StyledInput, { StyledInputContainer } from './StyledInput'
import withSpacing from './WithSpacing'

// Maybe this is too many levels, but attempting to extend StyledInput with additional background image
// is not returning all the functionality or base styles I get from StyledInput::: styled(StyledInput) is failing
// So instead we are using a simple style object to override
//----
// Single select components need to re-create the icon, but not as an "icon" since it needs to be clickable via standard browser events
// that can't be recreated via JS (for security reasons)
const compactSelectStyles = {
  backgroundImage: `url('data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB2aWV3Qm94PSIwIDAgMjQgMjQiIHhtbDpzcGFjZT0icHJlc2VydmUiIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCI+PGcgY2xhc3M9Im5jLWljb24td3JhcHBlciIgZmlsbD0iI2EwYTBhMiI+PHBvbHlnb24gZmlsbD0iI2EwYTBhMiIgcG9pbnRzPSIxMiwyIDE4LDEwIDYsMTAgIj48L3BvbHlnb24+IDxwb2x5Z29uIGRhdGEtY29sb3I9ImNvbG9yLTIiIGZpbGw9IiNhMGEwYTIiIHBvaW50cz0iMTIsMjIgMTgsMTQgNiwxNCAiPjwvcG9seWdvbj48L2c+PC9zdmc+')`,
  backgroundPosition: `right 5px center`,
  backgroundRepeat: `no-repeat`,
  backgroundSize: 13
}

interface InputChoiceSetProps {
  choices: unknown[]
  id?: string
  isMultiSelect?: boolean
  isRequired?: boolean
  label?: string
  style?: string
}

class InputChoiceSet extends Component<InputChoiceSetProps> {
  static displayName = 'InputChoiceSet'
  static defaultProps = {
    choices: [],
    style: 'expanded'
  }

  constructor(props: any) {
    super(props)

    this.state = {
      selectedValues: []
    }

    this.getValue = this.getValue.bind(this)
  }

  setValueFromCompact(setValue: any) {
    return (event: any) => {
      let value = event.target.value
      let selectedValues = []
      // Create a comma delimited list of the values (if multiple)
      if (this.props.isMultiSelect) {
        for (let i = 0; i < event.target.options.length; i++) {
          const option = event.target.options[i]
          option.selected && selectedValues.push(option.value)
          value = selectedValues.join(',')
        }
      } else {
        selectedValues = event.target.value
      }
      this.setState({ selectedValues })
      setValue(value)
    }
  }

  setValueFromExpanded(setValue: any) {
    return (val: any) => {
      let value = val
      if (Array.isArray(value)) {
        value = val.join(',')
      }
      this.setState({
        selectedValues: val
      })
      setValue(value)
    }
  }

  getValue(value: any) {
    const { isMultiSelect, style, choices } = this.props
    // For single select boxes we default to selecting the first element
    if (!isMultiSelect && style === 'compact' && !value && choices.length > 0) {
      // @ts-expect-error
      return choices[0].value
    } else {
      return value
    }
  }

  renderCompact(setValue: any, value: any, error: any) {
    // @ts-expect-error
    const { selectedValues } = this.state
    const { choices, isMultiSelect, isRequired, label } = this.props
    const val = isMultiSelect ? selectedValues : value

    const additionalStyles = isMultiSelect ? {} : compactSelectStyles
    return (
      <StyledInput
        // @ts-expect-error
        as={'select'}
        style={additionalStyles}
        multiple={isMultiSelect}
        value={val}
        onChange={this.setValueFromCompact(setValue)}
        error={error}
        required={isRequired}
        label={label}
      >
        {choices.map((choice: any, choiceIndex: any) => {
          return (
            <option key={choiceIndex} value={choice.value == null ? '' : choice.value}>
              {choice.title}
            </option>
          )
        })}
      </StyledInput>
    )
  }

  renderExpanded(setValue: any, value: any, error: any) {
    // @ts-expect-error
    const { selectedValues } = this.state
    const { isMultiSelect, choices, id, isRequired, label } = this.props
    const GroupComponent = isMultiSelect ? CustomCheckboxGroup : RadioGroup
    const ChoiceType = isMultiSelect ? Checkbox : Radio
    const val = isMultiSelect ? selectedValues : value

    return (
      <StyledInputContainer required={isRequired} error={error} label={label}>
        <GroupComponent selectedValue={val} onChange={this.setValueFromExpanded(setValue)} name={id}>
          {choices.map((choice: any, choiceIndex: any) => {
            return (
              <StyledToggleComponent
                as={ChoiceType}
                key={choiceIndex}
                value={choice.value || choice.title}
                title={choice.title}
              />
            )
          })}
        </GroupComponent>
      </StyledInputContainer>
    )
  }

  render() {
    const { style } = this.props

    return (
      // @ts-expect-error
      <Input
        {...this.props}
        valueOverride={this.getValue}
        render={(setValue, value, error) =>
          style === 'compact' ? this.renderCompact(setValue, value, error) : this.renderExpanded(setValue, value, error)
        }
      />
    )
  }
}

export default withSpacing(InputChoiceSet)
export { InputChoiceSet }

// Map `selectedValue` prop to `value` prop
function CustomCheckboxGroup({
  selectedValue,
  ...rest
}: Omit<ComponentProps<typeof CheckboxGroup>, 'value'> & {
  selectedValue: ComponentProps<typeof CheckboxGroup>['value']
}) {
  return <CheckboxGroup {...rest} value={selectedValue} />
}
